<template>
  <div>
    <ui-dialog
      class="tags-manager-dialog"
      :show="isOpen"
      max-width="687"
      @input="close"
    >
      <template #header>
        <div class="dialog-header">
          <h3 class="dialog-title">
            {{ dialogTitle }}
          </h3>
        </div>
      </template>
      <template v-if="canUseAutoCategorization">
        <TagManagerDialogInfoBox
          class="mb-2"
          :text="
            $t('message.reviews.dialogs.howDoesAutomaticCategorizationWork')
          "
        >
          <ui-button
            color="primary"
            outlined
            @click="handleShowAutoCategorizeAlert"
          >
            {{ $t("message.common.learnMore") }}
          </ui-button>
        </TagManagerDialogInfoBox>
        <TagManagerDialogInfoBox
          class="mb-2"
          :text="$t('message.reviews.dialogs.autoCategorizeOldReviews')"
        >
          <ui-button color="primary" :href="`mailto:${supportEmail}`">
            {{ $t("message.reviews.dialogs.contact") }}
          </ui-button>
        </TagManagerDialogInfoBox>
      </template>
      <TagsManagerListContent
        ref="listContent"
        :can-manage-tags="canManageTags"
        :tags="tags"
        :is-open="isOpen"
        :is-loading="isTagLoading"
        :categorization-enabled="canUseAutoCategorization"
        show-auto-categorization-icon
        :show-sentiment-selector="false"
        @list-update="onListUpdate"
        @select-current-tag="selectCurrentTagHandler"
      />
    </ui-dialog>
    <template v-if="canUseAutoCategorization">
      <TagAlertDialog
        v-if="showAutoCategorizeAlert"
        :is-open="showAutoCategorizeAlert"
        :title="$t('message.reviews.dialogs.autoCategorizationTitle')"
        :content="$t('message.reviews.dialogs.autoCategorizationContent')"
        @close="showAutoCategorizeAlert = false"
      >
        <v-checkbox
          v-if="showAutoCategorizeAlertCheckbox"
          class="alert__checkbox"
          :label="$t('message.common.dontShowItAgain')"
          @click="toggleDialogVisibility"
        ></v-checkbox
      ></TagAlertDialog>
      <TagAlertDialog
        v-if="showAfterEditAlert"
        :is-open="showAfterEditAlert"
        :title="$t('message.reviews.dialogs.alertAfterEditTitle')"
        :content="
          $t('message.reviews.dialogs.alertAfterEditContent', [currentTag.name])
        "
        text-small
        :show-close-button="false"
      >
        <div class="alert__checkbox-container">
          <v-icon>icon-robot</v-icon>
          <span>{{ $t("message.reviews.autoCategorization") }}</span>
          <v-switch
            hide-details
            inset
            v-model="autoCategorizationEnabled"
          ></v-switch>
        </div>
        <ui-button
          color="primary"
          class="alert__submit"
          @click="setAutoCategorization"
        >
          {{ $t("message.common.save") }}
        </ui-button>
      </TagAlertDialog>
    </template>
  </div>
</template>

<script>
import serviceHandler from "@/helpers/handlers/service";
import { permissionsInfo } from "@/helpers/mixins/permissions-info";
import TagsManagerListContent from "@/components/Tags/TagsManagerListContent";
import TagAlertDialog from "@/components/Tags/TagAlertDialog";
import { LOCAL_STORAGE_KEYS } from "@/enums/local-storage";
import tagsService from "@/services/tags";
import { isFeatureFlagEnabled, FEATURE_FLAG } from "@/helpers/posthog";
import { GTM_EVENT_NAME } from "@/enums/gtm-events";
import constants from "@/config/constants";
import TagManagerDialogInfoBox from "@/components/Tags/TagsManagerDialogInfoBox";
import { PLAN_RESTRICTIONS } from "@/enums/plan-restrictions";
import { planRestrictionsCheck } from "@/helpers/plan-restrictions-check";

export default {
  name: "TagsManagerDialog",
  components: {
    TagsManagerListContent,
    TagAlertDialog,
    TagManagerDialogInfoBox
  },
  mixins: [permissionsInfo],
  props: {
    isOpen: {
      type: Boolean,
      default: false
    },
    tags: {
      type: Array,
      default: () => []
    },
    isTagLoading: {
      type: Boolean,
      required: true
    }
  },
  computed: {
    dialogTitle() {
      if (this.canManageTags) {
        return this.$t("message.reviews.tags.manageTags");
      } else {
        return "";
      }
    },
    canUseAutoCategorization() {
      return planRestrictionsCheck(
        PLAN_RESTRICTIONS.CAN_USE_AI_REVIEWS_CATEGORIZATION
      );
    }
  },
  data() {
    return {
      FEATURE_FLAG,
      showAfterEditAlert: false,
      showAutoCategorizeAlert: false,
      showAutoCategorizeAlertCheckbox: false,
      currentTag: null,
      modifiedTagId: null,
      localTags: [],
      phrasesGroup: { phrases: [] },
      allPhrases: { results: [] },
      selectedPhrases: null,
      isAlertDialogHidden: false,
      autoCategorizationEnabled: false,
      isLoading: false,
      supportEmail: constants.centerAiEmail,
      defaultPaging: {
        page_size: 10,
        page: 1
      }
    };
  },

  watch: {
    tags(current) {
      this.localTags = [...current];
    },
    isOpen: {
      immediate: true,
      handler(value) {
        if (value) {
          this.localTags = [...this.tags];
          this.$nextTick(() => {
            this.showAutoCategorizeAlertCheckbox = this.showAutoCategorizeAlert = !localStorage.getItem(
              LOCAL_STORAGE_KEYS.HIDE_AUTO_CATEGORIZE_DIALOG
            );
          });
        }
      }
    }
  },

  methods: {
    isFeatureFlagEnabled,

    handleShowAutoCategorizeAlert() {
      this.showAutoCategorizeAlert = true;
      this.$gtm.trackCustomEvent({
        event: GTM_EVENT_NAME.REVIEW_CATEGORY_AUTO_SHOW_HELP
      });
    },
    async setAutoCategorization() {
      if (this.autoCategorizationEnabled) {
        await serviceHandler(
          tagsService.editTag(this.modifiedTagId, {
            is_auto_categorized: true
          })
        );

        this.$gtm.trackCustomEvent({
          event: GTM_EVENT_NAME.REVIEW_CATEGORY_AUTO_STATUS_CHANGE,
          category_name: this.currentTag.name,
          category_id: this.currentTag.id,
          component: "category_keywords_mgmt",
          new_status: "on"
        });
      }
      this.showAfterEditAlert = false;
      this.autoCategorizationEnabled = false;
    },
    toggleDialogVisibility() {
      if (this.showAutoCategorizeAlert) {
        localStorage.setItem(
          LOCAL_STORAGE_KEYS.HIDE_AUTO_CATEGORIZE_DIALOG,
          true
        );
      } else {
        localStorage.removeItem(LOCAL_STORAGE_KEYS.HIDE_AUTO_CATEGORIZE_DIALOG);
      }
    },
    onListUpdate() {
      this.$emit("list-update", [...this.localTags]);
    },

    close() {
      this.$emit("close");
    },

    selectCurrentTagHandler(tag) {
      this.currentTag = tag;
    },
    closeAlert() {
      this.showAlert = false;
      this.isAlertDialogHidden = localStorage.getItem(
        LOCAL_STORAGE_KEYS.HIDE_AUTO_CATEGORIZE_DIALOG
      );
    }
  }
};
</script>

<style lang="scss" scoped>
.dialog-header {
  &__back-icon {
    &.v-icon {
      transform: rotate(-90deg);

      &:before {
        @include font-size(24px);
      }
    }
  }
  &__link {
    text-decoration: underline;
    color: $primary;
    @include font-size(14px);
    font-weight: 600;
    margin-top: $spacing * 2;
    cursor: pointer;
  }
}
.tag-input {
  display: flex;
}

.tags-header {
  padding: $spacing * 2.5 0;
  border-top: 1px solid $light-3;
  border-bottom: 1px solid $light-3;

  &--no-border {
    border: none;
  }
}

.tags-body-header {
  color: $dark-blue-3;
  @include font-size(14px);
  line-height: 17px;
  font-weight: 600;
  padding: $spacing * 2 0;
}

.tags-body {
  flex: 1;
  height: 300px;
  overflow-x: hidden;
  overflow-y: auto;
}

.tags-empty {
  @include font-size(14px);
  line-height: 17px;
  font-weight: 500;
  color: rgba($dark-blue-3, 0.4);
}

.no-permission {
  text-align: center;
  white-space: pre-line;
  margin-bottom: $spacing * 8;
}

.dialog-title {
  margin-top: $spacing * 2;
  min-height: 28px;
}
.alert {
  border-radius: 25px;
  background: white;
  padding: $spacing * 4;

  &__submit {
    margin: auto;
    display: block;
  }

  &__checkbox-container {
    display: flex;
    align-items: center;
    justify-content: center;
    border: 1px solid $gray-300;
    max-width: 250px;
    margin: $spacing * 4 auto;
    border-radius: 24px;
    height: 50px;
    padding: 0 $spacing * 2.5;
    span {
      line-height: 1rem;
      @include font-size(14px);
      margin-left: $spacing * 1.5;
    }

    ::v-deep.v-input {
      margin: 0;
      display: flex;
      align-items: center;
      justify-content: center;
      padding: 0;
    }
  }

  &__checkbox {
    margin-top: $spacing * 5;

    ::v-deep.v-label {
      @include font-size(14px);
    }
  }
}
</style>
